import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { commonConstant } from '@/hotdesk-ui/constants/common.constant';

/** Query keys that encode explore location (not listing filters). */
export const EXPLORE_LOCATION_PARAM_KEYS = [
    'lat',
    'lon',
    'log', // legacy alias
    'lng', // legacy alias
    'countryCode',
    'region'
] as const;

/** Listing filters that belong in the shareable explore URL. */
export const EXPLORE_FILTER_PARAM_KEYS = [
    'typeIn',
    'frequency',
    'priceGte',
    'priceLte',
    'amenitiesIdIn',
    'paymentTypeIn',
    'filterCapOrQtyGte',
    'quickBooking',
    'ratingGte',
    'userSubscriptionIdIn'
] as const;

type SearchParamsLike = {
    get(name: string): string | null;
};

const emptyAddressComponents =
    [] as unknown as GeoCodeResult['address_components'];
const emptyTypes = [] as unknown as GeoCodeResult['types'];

function parseCoord(value: string | null): number | null {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

/** Resolve an ISO-2 country code (e.g. EG) to a localized display name (Egypt). */
export function countryCodeToDisplayName(
    countryCode: string,
    locale = 'en'
): string {
    const code = countryCode.trim().toUpperCase();
    if (code.length !== 2) return countryCode.trim();
    try {
        return (
            new Intl.DisplayNames([locale], { type: 'region' }).of(code) ||
            code
        );
    } catch {
        return code;
    }
}

/** Read longitude from URL (`lon`, with legacy `log` / `lng` fallbacks). */
export function getLonFromSearchParams(params: SearchParamsLike): number | null {
    return parseCoord(
        params.get('lon') ?? params.get('log') ?? params.get('lng')
    );
}

/** True when this geo is a country-wide search (ISO code, not radius). */
export function isCountryWideGeo(geo: GeoCodeResult | null | undefined): boolean {
    return !!geo?.types?.includes('country') && !!geo?.country_sort;
}

/**
 * True when this geo is a whole state ("California", "Cairo Governorate").
 * Same problem as a country: a radius around its centre covers a sliver of
 * it, so it is searched by name within its country instead.
 */
export function isRegionWideGeo(geo: GeoCodeResult | null | undefined): boolean {
    return (
        !!geo?.types?.includes('administrative_area_level_1') &&
        !!geo?.country_sort &&
        !!geo?.region_name
    );
}

/** True for a country- or state-wide search: no meaningful point to centre on. */
export function isAreaWideGeo(geo: GeoCodeResult | null | undefined): boolean {
    return isCountryWideGeo(geo) || isRegionWideGeo(geo);
}

/** Filters for a country/state-wide search, for `getListingsDataParams`. */
export type GeoAreaScope = { countryCode: string; region?: string };

/**
 * `countryCode` (+ `region` for a state) to fetch an area-wide search by,
 * or null when the geo is a point to search around. The API matches the
 * region name only within `countryCode`, so the two always travel together.
 */
export function geoAreaScope(
    geo: GeoCodeResult | null | undefined
): GeoAreaScope | null {
    if (isRegionWideGeo(geo)) {
        return {
            countryCode: geo!.country_sort as string,
            region: geo!.region_name
        };
    }
    if (isCountryWideGeo(geo)) {
        return { countryCode: geo!.country_sort as string };
    }
    return null;
}

/**
 * Circle around a geo's centre, for `getListingsDataParams`. With a state
 * scope it becomes the radius search to fall back on when the API knows no
 * region by that name (Irish counties, Taipei); otherwise it is ignored.
 */
export function geoCentreCircle(
    geo: GeoCodeResult | null | undefined,
    radius: number
): { lat: number; lng: number; radius: number } | null {
    if (geo?.lat == null || geo?.log == null) return null;
    return { lat: geo.lat as number, lng: geo.log as number, radius };
}

/** Stable key for an area-wide search ("US|California"), for dedupe checks. */
export function geoAreaKey(scope: GeoAreaScope | null): string | null {
    return scope ? `${scope.countryCode}|${scope.region ?? ''}` : null;
}

/**
 * Cairo fallback coords from remote/common default.
 * Only meaningful together with source === 'remote-default' — real GPS in
 * downtown Cairo can share nearly the same coordinates (e.g. Haret Bondoq).
 */
export function isRemoteDefaultCoords(
    lat?: number | null,
    lon?: number | null
): boolean {
    if (lat == null || lon == null) return false;
    const d = commonConstant.defaultLocation;
    return (
        Math.abs(lat - d.latitude) < 0.0001 &&
        Math.abs(lon - d.longitude) < 0.0001
    );
}

/** True when this geo is the synthetic Cairo fallback (not a real GPS fix). */
export function isRemoteDefaultGeo(
    geo: GeoCodeResult | null | undefined
): boolean {
    if (!geo) return false;
    if (geo.source === 'remote-default') return true;
    // Legacy persisted records without source: only exact default coords.
    if (!geo.source && isRemoteDefaultCoords(geo.lat, geo.log)) return true;
    return false;
}

/** Location worth putting in the URL (user search / GPS — not Cairo default). */
export function shouldPersistGeoInUrl(
    geo: GeoCodeResult | null | undefined
): boolean {
    if (!geo) return false;
    if (isRemoteDefaultGeo(geo)) return false;
    if (isAreaWideGeo(geo)) return true;
    if (geo.lat == null || geo.log == null) return false;
    return true;
}

/**
 * Write location into URL search params so the explore URL is shareable and
 * produces the same listing/workspace results when opened elsewhere.
 */
export function applyGeoToSearchParams(
    params: URLSearchParams,
    geo: GeoCodeResult | null | undefined
): void {
    for (const key of EXPLORE_LOCATION_PARAM_KEYS) {
        params.delete(key);
    }

    if (!geo || !shouldPersistGeoInUrl(geo)) return;

    const scope = geoAreaScope(geo);
    if (scope) {
        params.set('countryCode', scope.countryCode);
        if (scope.region) params.set('region', scope.region);
        return;
    }

    if (geo.lat != null && geo.log != null) {
        params.set('lat', String(geo.lat));
        params.set('lon', String(geo.log));
    }
}

/** Copy location keys from one params object onto another. */
export function copyLocationSearchParams(
    from: SearchParamsLike,
    to: URLSearchParams
): void {
    const lat = from.get('lat');
    const lon =
        from.get('lon') ?? from.get('log') ?? from.get('lng');
    const countryCode = from.get('countryCode');
    const region = from.get('region');

    if (countryCode) to.set('countryCode', countryCode);
    if (countryCode && region) to.set('region', region);
    if (lat) to.set('lat', lat);
    if (lon) {
        to.set('lon', lon);
        to.delete('log');
        to.delete('lng');
    }
}

/** Strip location keys before treating URL params as listing filters. */
export function omitLocationParamsFromRecord(
    record: Record<string, string>
): Record<string, string> {
    const next = { ...record };
    for (const key of EXPLORE_LOCATION_PARAM_KEYS) {
        delete next[key];
    }
    return next;
}

function readSearchParam(
    params:
        | SearchParamsLike
        | URLSearchParams
        | Record<string, string | string[] | number | undefined | null>,
    key: string
): string | null {
    if (
        params &&
        typeof (params as SearchParamsLike).get === 'function'
    ) {
        return (params as SearchParamsLike).get(key);
    }
    const raw = (
        params as Record<string, string | string[] | number | undefined | null>
    )[key];
    if (raw == null) return null;
    const value = Array.isArray(raw) ? raw[0] : raw;
    if (value == null || value === '') return null;
    return String(value);
}

/**
 * Pull only listing-filter keys from the explore URL (same set written by
 * the filters modal / type pills). Ignores location, `active`, `lang`, etc.
 */
export function filtersFromSearchParams(
    params:
        | SearchParamsLike
        | URLSearchParams
        | Record<string, string | string[] | number | undefined | null>
): Record<string, string> {
    const out: Record<string, string> = {};
    for (const key of EXPLORE_FILTER_PARAM_KEYS) {
        const value = readSearchParam(params, key)?.trim();
        if (value) out[key] = value;
    }
    return out;
}

/** Keep only known explore filter keys (safe to send to the listings API). */
export function pickExploreApiFilters(
    filters: Record<string, string> | null | undefined
): Record<string, string> {
    if (!filters) return {};
    const out: Record<string, string> = {};
    for (const key of EXPLORE_FILTER_PARAM_KEYS) {
        const value = filters[key];
        if (value != null && String(value).trim() !== '') {
            out[key] = String(value);
        }
    }
    return out;
}

/**
 * Build a minimal GeoCodeResult from explore URL params.
 * Enough for listing/workspace fetches (`lat`/`lon`, `countryCode`, or
 * `countryCode` + `region` for a state).
 * Country codes are expanded to a display name for the search input (EG → Egypt).
 */
export function geoFromSearchParams(
    params: SearchParamsLike,
    locale = 'en'
): GeoCodeResult | null {
    const countryCode = params.get('countryCode')?.trim();
    const region = params.get('region')?.trim();
    const lat = parseCoord(params.get('lat'));
    const lon = getLonFromSearchParams(params);

    if (countryCode && region) {
        const code = countryCode.toUpperCase();
        const countryName = countryCodeToDisplayName(code, locale);
        return {
            address_components: emptyAddressComponents,
            formatted_address: `${region}, ${countryName}`,
            geometry: {
                location: { lat: lat ?? 0, lng: lon ?? 0 },
                location_type: 'APPROXIMATE'
            },
            place_id: '',
            plus_code: { compound_code: '', global_code: '' },
            types: [
                'administrative_area_level_1'
            ] as unknown as GeoCodeResult['types'],
            country: countryName,
            country_sort: code,
            state: region,
            region_name: region,
            ...(lat != null ? { lat } : {}),
            ...(lon != null ? { log: lon } : {})
        };
    }

    if (countryCode) {
        const code = countryCode.toUpperCase();
        const displayName = countryCodeToDisplayName(code, locale);
        return {
            address_components: emptyAddressComponents,
            formatted_address: displayName,
            geometry: {
                location: {
                    lat: lat ?? 0,
                    lng: lon ?? 0
                },
                location_type: 'APPROXIMATE'
            },
            place_id: '',
            plus_code: { compound_code: '', global_code: '' },
            types: ['country'] as unknown as GeoCodeResult['types'],
            country: displayName,
            country_sort: code,
            ...(lat != null ? { lat } : {}),
            ...(lon != null ? { log: lon } : {})
        };
    }

    if (lat == null || lon == null) return null;

    return {
        address_components: emptyAddressComponents,
        formatted_address: `${lat.toFixed(4)}, ${lon.toFixed(4)}`,
        geometry: {
            location: { lat, lng: lon },
            location_type: 'APPROXIMATE'
        },
        place_id: '',
        plus_code: { compound_code: '', global_code: '' },
        types: emptyTypes,
        lat,
        log: lon
    };
}

/** Attach current location to a workspaces navigation URLSearchParams. */
export function appendExploreLocationParams(
    params: URLSearchParams,
    geo: GeoCodeResult | null | undefined
): void {
    applyGeoToSearchParams(params, geo);
}

/**
 * Long-term Offices explore filter (month + yearly).
 * The listings API treats comma-separated `frequency` as a union (OR).
 * IWG/Regus hour/day-only inventory is loaded in a separate request and
 * merged client-side — it is never part of this URL filter.
 */
export const LONG_TERM_OFFICES_FREQUENCY = 'month,annual';

/** Secondary fetch for IWG/Regus quote-only cards under Long-term Offices. */
export const LONG_TERM_IWG_QUOTE_FREQUENCY = 'day,hour';

/** True when explore frequency includes long-term plans (month and/or annual). */
export function isLongTermFrequency(
    frequency: string | null | undefined
): boolean {
    if (!frequency) return false;
    const units = new Set(
        frequency.split(',').map((s) => s.trim()).filter(Boolean)
    );
    return units.has('month') || units.has('annual');
}

/**
 * Keep long-term explore URLs free of short-term units (strip day/hour if an
 * older link still has them). Preserve the user's plan choice — do not expand
 * a sole `month` or `annual` into `month,annual`.
 */
export function normalizeLongTermOfficesFrequency(
    frequency: string | null | undefined
): string | null | undefined {
    if (!frequency || !isLongTermFrequency(frequency)) return frequency;
    const units = frequency
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    const longTermUnits = units.filter(
        (u) => u === 'month' || u === 'annual'
    );
    // Already only long-term plans — leave month-only / annual-only alone.
    if (longTermUnits.length === units.length) {
        return frequency;
    }
    // Older Long-term Offices links mixed in day/hour; keep the long-term units.
    return longTermUnits.join(',') || LONG_TERM_OFFICES_FREQUENCY;
}

/**
 * Listing exposes a monthly price on `rules.price`.
 * Used so IWG/Regus with month rates keep normal explore cards under long-term
 * filters, while hour/day-only inventory becomes Request Quote.
 */
export function listingHasMonthPrice(price: unknown): boolean {
    if (!price || typeof price !== 'object') return false;
    return Object.prototype.hasOwnProperty.call(price, 'month');
}

/**
 * IWG/Regus card should open the quote form (no price / no details) when the
 * user is on Long-term Offices (month/year in filters) and the listing has no
 * monthly rate — only hour/day inventory.
 */
export function shouldRequestIwgLongTermQuote(
    listing: { rules?: { price?: unknown } } | null | undefined,
    frequency: string | null | undefined,
    isProviderListing: boolean
): boolean {
    return (
        isProviderListing &&
        isLongTermFrequency(frequency) &&
        !listingHasMonthPrice(listing?.rules?.price)
    );
}

/**
 * Short-term unit an IWG/Regus quote card can still be booked by (day first,
 * as the closer match to a long-term search), or null when it has neither.
 */
export function iwgBookableUnit(price: unknown): 'day' | 'hour' | null {
    if (!price || typeof price !== 'object') return null;
    if (Object.prototype.hasOwnProperty.call(price, 'day')) return 'day';
    if (Object.prototype.hasOwnProperty.call(price, 'hour')) return 'hour';
    return null;
}

/** Short-term units (hour, then day) an IWG/Regus quote card can be booked by. */
export function iwgBookableUnits(price: unknown): Array<'hour' | 'day'> {
    if (!price || typeof price !== 'object') return [];
    return (['hour', 'day'] as const).filter((unit) =>
        Object.prototype.hasOwnProperty.call(price, unit)
    );
}

/**
 * Point a listing URL at its bookable unit: long-term `frequency` is swapped
 * for day/hour so the details page opens on a rate the listing actually has.
 */
export function withBookableFrequency(url: string, price: unknown): string {
    const unit = iwgBookableUnit(price);
    if (!unit) return url;
    const [path, query = ''] = url.split('?');
    const params = new URLSearchParams(query);
    params.set('frequency', unit);
    return `${path}?${params.toString()}`;
}

/** Keep IWG/Regus day/hour listings that have no month rate (quote-only). */
export function filterIwgQuoteOnlyListings<
    T extends { rules?: { price?: unknown } }
>(listings: T[], isProviderListing: (listing: T) => boolean): T[] {
    return listings.filter(
        (listing) =>
            isProviderListing(listing) &&
            !listingHasMonthPrice(listing.rules?.price)
    );
}

/** Explore URL for "Long-term offices" — long-term plans + the user's location. */
export function buildLongTermOfficesExploreHref(
    geo: GeoCodeResult | null | undefined,
    lang?: string
): string {
    const params = new URLSearchParams();
    if (lang) params.set('lang', lang);
    params.set('frequency', LONG_TERM_OFFICES_FREQUENCY);
    params.set('active', 'split');
    appendExploreLocationParams(params, geo);
    return `/workspaces?${params.toString()}`;
}

/**
 * Prefer a price unit from a (possibly comma-separated) `frequency` URL param.
 * Returns null when none of the requested units exist on the listing.
 */
export function preferredPriceUnitFromFrequency(
    frequency: string | null | undefined,
    priceKeys: string[]
): string | null {
    if (!frequency) return null;
    for (const unit of frequency.split(',').map((s) => s.trim()).filter(Boolean)) {
        if (priceKeys.includes(unit)) return unit;
    }
    return null;
}

/**
 * Sync geo into the current browser URL (reads `window.location.search` so
 * prior replaceState location updates are not lost to stale Next searchParams).
 */
export function replaceExploreUrlWithGeo(
    geo: GeoCodeResult | null | undefined,
    extra?: Record<string, string | null | undefined>
): void {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    if (extra) {
        Object.entries(extra).forEach(([k, v]) => {
            if (v == null || v === '') params.delete(k);
            else params.set(k, v);
        });
    }
    applyGeoToSearchParams(params, geo);
    const qs = params.toString();
    window.history.replaceState(
        null,
        '',
        qs ? `?${qs}` : window.location.pathname
    );
}
